// src/contexts/WalletContext.tsx (COMPLETE UPDATED VERSION WITH TRANSACTION RESULT HANDLING)
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { WalletContextType } from '../types';
import solanaService from '../services/solana.service';
import revshareService from '../services/revshare.service';
import phantomDeeplinkService from '../services/phantom-deeplink.service';

const WalletContext = createContext<WalletContextType>({
  isConnected: false,
  publicKey: null,
  balance: 0,
  connecting: false,
  connect: async () => {},
  disconnect: async () => {},
});

export const useWallet = () => useContext(WalletContext);

export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [balance, setBalance] = useState(0);
  const [connecting, setConnecting] = useState(false);

  const getProvider = () =>
    (typeof window !== 'undefined' && (window as any).solana?.isPhantom
      ? (window as any).solana
      : null);

  const extractAddress = (pk: any): string | null => {
    if (!pk) return null;
    if (typeof pk.toBase58 === 'function') return pk.toBase58();
    if (typeof pk.toString === 'function') return pk.toString();
    try {
      return String(pk);
    } catch {
      return null;
    }
  };

  const updateBalance = useCallback(async (address: string) => {
    try {
      const bal = await solanaService.getBalance(address);
      setBalance(bal);
    } catch (error) {
      console.error('Failed to update balance:', error);
    }
  }, []);

  // Handle successful connection after redirect
  const completeConnection = useCallback(async (address: string) => {
    try {
      console.log('Completing connection for address:', address);
      setPublicKey(address);
      setIsConnected(true);
      await updateBalance(address);
      await revshareService.initialize(address);
      localStorage.setItem('wallet_connected', 'true');
      console.log('Connection completed successfully');
    } catch (error) {
      console.error('Failed to complete connection:', error);
      throw error;
    }
  }, [updateBalance]);

  // NEW: Handle transaction result feedback from Phantom redirects
  const handleTransactionResult = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const phantomTransaction = urlParams.get('phantom_transaction');
    
    if (phantomTransaction) {
      // Get the stored transaction result
      const result = localStorage.getItem('phantom_transaction_result');
      if (result) {
        try {
          const data = JSON.parse(result);
          
          if (phantomTransaction === 'success') {
            console.log('Transaction completed successfully!');
            
            // Show success message based on transaction type
            switch (data.type) {
              case 'transaction_signature':
                alert(`Transaction successful! Signature: ${data.signature.slice(0, 20)}...`);
                break;
              case 'signed_transactions':
                alert(`Batch transactions signed successfully! (${data.transactions.length} transactions)`);
                break;
              case 'message_signature':
                alert('Message signed successfully!');
                break;
              default:
                alert('Transaction completed successfully!');
            }
            
          } else if (phantomTransaction === 'error') {
            console.log('Transaction failed:', data.error);
            alert(`Transaction failed: ${data.error}`);
          }
          
          // Clean up stored result
          localStorage.removeItem('phantom_transaction_result');
          
          // Clean URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          
        } catch (error) {
          console.error('Failed to parse transaction result:', error);
          // Clean URL even if parsing failed
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    }
  }, []);

  const connect = useCallback(async () => {
    try {
      setConnecting(true);

      // Mobile detection
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const provider = getProvider();
      const isInPhantomBrowser = !!provider;

      console.log('Connection attempt:', { isMobile, isInPhantomBrowser });

      // First check if we have a stored deeplink connection
      if (phantomDeeplinkService.hasValidSession()) {
        const storedPublicKey = phantomDeeplinkService.getStoredPublicKey();
        if (storedPublicKey) {
          console.log('Using stored deeplink connection:', storedPublicKey);
          await completeConnection(storedPublicKey);
          return;
        }
      }

      // On mobile but not in Phantom browser - use deeplink
      if (isMobile && !isInPhantomBrowser) {
        console.log('Attempting deeplink connection...');
        
        // Clear any timed out attempts
        if (phantomDeeplinkService.isConnectionTimedOut()) {
          console.log('Clearing timed out connection attempt');
          phantomDeeplinkService.clearSession();
        }
        
        try {
          // This will redirect to Phantom app
          await phantomDeeplinkService.connect();
        } catch (error: any) {
          if (error?.message?.includes('Redirecting')) {
            console.log('Redirecting to Phantom app for deeplink connection');
            // This is expected - the user is being redirected
            return;
          }
          throw error;
        }
        return; // The actual connection will happen via redirect
      }

      // Check if Phantom is installed (desktop or Phantom browser)
      if (!solanaService.isPhantomInstalled()) {
        if (isMobile) {
          throw new Error('Please install Phantom wallet app');
        } else {
          window.open('https://phantom.app/', '_blank');
          throw new Error('Please install Phantom wallet extension');
        }
      }

      // Connect to Phantom (desktop or Phantom browser)
      const key = await solanaService.connect();
      await completeConnection(key);

      console.log('Wallet connected successfully:', key);
    } catch (error: any) {
      console.error('Failed to connect wallet:', error);

      if (error?.message?.includes?.('User rejected')) {
        console.log('Connection cancelled by user');
      } else if (error?.message?.includes?.('Redirecting')) {
        console.log('Redirecting to Phantom app...');
        // Don't show error for redirects
      } else if (error?.message?.includes?.('Phantom')) {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          console.log('Phantom app connection required');
        } else {
          console.log('Phantom extension required');
        }
      } else {
        console.error('Connection error:', error);
      }
      throw error; // Re-throw so Header component can handle display
    } finally {
      setConnecting(false);
    }
  }, [completeConnection]);

  const disconnect = useCallback(async () => {
    try {
      // Clear deeplink session
      phantomDeeplinkService.clearSession();
      
      // Disconnect provider if available
      await solanaService.disconnect();
      setIsConnected(false);
      setPublicKey(null);
      setBalance(0);

      // Clear RevShare user data
      revshareService.clearUser();

      // Clear stored connection state
      localStorage.removeItem('wallet_connected');

      console.log('Wallet disconnected');
    } catch (error) {
      console.error('Failed to disconnect wallet:', error);
    }
  }, []);

  // Listen for Phantom events (desktop/browser only)
  useEffect(() => {
    const provider = getProvider();
    if (!provider) return;

    const handleConnect = async (pk: any) => {
      const addr = extractAddress(pk) ?? extractAddress(provider.publicKey);
      if (!addr) return;
      console.log('Phantom connect event:', addr);
      await completeConnection(addr);
    };

    const handleAccountChange = async (pk: any) => {
      const addr = extractAddress(pk);
      if (addr) {
        console.log('Account changed:', addr);
        setPublicKey(addr);
        await updateBalance(addr);
        revshareService.setCurrentUser(addr);
      } else {
        // Wallet locked or disconnected
        await disconnect();
      }
    };

    const handleDisconnect = async () => {
      console.log('Phantom disconnected');
      await disconnect();
    };

    provider.on('connect', handleConnect);
    provider.on('accountChanged', handleAccountChange);
    provider.on('disconnect', handleDisconnect);

    return () => {
      provider.removeListener('connect', handleConnect);
      provider.removeListener('accountChanged', handleAccountChange);
      provider.removeListener('disconnect', handleDisconnect);
    };
  }, [completeConnection, updateBalance, disconnect]);

  // NEW: Handle transaction result feedback from Phantom redirects
  useEffect(() => {
    handleTransactionResult();
  }, [handleTransactionResult]);

  // Auto-connect logic for existing sessions
  useEffect(() => {
    const initializeWallet = async () => {
      console.log('Checking for existing connections...');

      // Check for deeplink connection first
      if (phantomDeeplinkService.hasValidSession()) {
        const storedPublicKey = phantomDeeplinkService.getStoredPublicKey();
        if (storedPublicKey) {
          console.log('Auto-connecting with stored deeplink session:', storedPublicKey);
          try {
            await completeConnection(storedPublicKey);
            return;
          } catch (error) {
            console.error('Failed to auto-connect with stored session:', error);
            phantomDeeplinkService.clearSession();
          }
        }
      }

      // Check for regular Phantom connection
      const wasConnected = localStorage.getItem('wallet_connected') === 'true';
      if (!wasConnected || !solanaService.isPhantomInstalled()) {
        console.log('No previous connection found or Phantom not installed');
        return;
      }

      try {
        const provider = getProvider();
        if (provider?.isConnected) {
          const addr =
            extractAddress(provider.publicKey) ??
            extractAddress((window as any).solana?.publicKey);

          if (addr) {
            console.log('Auto-connected to existing Phantom session:', addr);
            await completeConnection(addr);
          } else {
            console.log('No valid address found, clearing connection state');
            localStorage.removeItem('wallet_connected');
          }
        } else {
          console.log('Provider not connected, clearing connection state');
          localStorage.removeItem('wallet_connected');
        }
      } catch (error) {
        console.error('Auto-connect failed:', error);
        localStorage.removeItem('wallet_connected');
      }
    };

    // Use a small delay to ensure DOM is ready
    const timer = setTimeout(initializeWallet, 100);
    return () => clearTimeout(timer);
  }, [completeConnection]);

  // Handle URL parameter cleanup and error display
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connectionError = urlParams.get('connection_error');
    
    if (connectionError === 'phantom_redirect') {
      console.warn('Connection error detected from redirect');
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // Periodically update balance
  useEffect(() => {
    if (isConnected && publicKey) {
      const interval = setInterval(() => {
        updateBalance(publicKey);
      }, 30000); // Every 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [isConnected, publicKey, updateBalance]);

  return (
    <WalletContext.Provider 
      value={{ 
        isConnected, 
        publicKey, 
        balance, 
        connecting, 
        connect, 
        disconnect 
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};